import React, { useState, useEffect } from "react";
import { GetLastUser, SetLastUser } from "../../services/usersService";
import {
	GetOrCreateReceiptInformation,
	UpdateReceiptInformation,
	GetCheckInSheet,
	UpdateExistingCheckInSheet,
} from "../../services/receiptInfomrationService";
import SampleScannedForm from "./sampleScannedForm";
import AllSamplesSubmitted from "./AllSamplesSubmitted";
import CheckInSheetSelector from "./CheckInSheetSelector";
import UserSelectorDialog from "../../vlp/components/common/UserSelectorDialog";
import { NotificationManager } from "react-notifications";
import { getFormattedDateTime } from "../../services/dateService";
import { TextField, InputAdornment } from "@mui/material";
import { Person as PersonIcon, Edit as EditIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsers, selectUsersData, selectUsersStatus } from "../../redux/usersSlice";

let defaultValues = {
	recordId: null,
	identifier: null,
	userId: null,
	dateReceied: null,
	receivedOn: "Wet Ice",
	coolerTemperature: null,
	deliveryMethod: "PickUp",
	coolerTempAcceptable: null,
	cocCompleted: null,
	containerIntact: null,
	withinHoldingTime: null,
	shortHoldingTime: null,
	sufficientVolume: null,
	noSplit: null,
	isRush: null,
	correctLabels: null,
	headSpacePresent: null,
	labComments: null,
	orderPayType:'',
	ph1: null,
	ph2: null,
	ph3: null,
	ph4: null,
	ph5: null,
	ph6: null,
	ph7: null,
	ph8: null,
	ph9: null,
	ph10: null,
	ph11: null,
	ph12: null,
	isKitPaid: false,
	willRushPay: false
};

export default function SampleScanned(props) {
	const [info, setInfo] = useState(); //the sample receipt information
	const [currentUser, setCurrentUser] = useState();
	const [selectedUser, setSelectedUser] = useState(null);
	const [userSelectorOpen, setUserSelectorOpen] = useState(false);
	const [userRequiredError, setUserRequiredError] = useState(false);
	const [loading, SetLoading] = useState(false);
	const [selectedSampleIds, setSelectedSampleIds] = useState([]);
	// "choose" = order already has submitted sheets, so ask what the user wants to do;
	// "new" = fill in another sheet; "edit" = correct an already-submitted sheet.
	const [mode, setMode] = useState("new");
	const [editingSheet, setEditingSheet] = useState(null);
	// The order-level response, kept aside so the sheet picker and "back" still work
	// after `info` has been swapped for a single sheet being edited.
	const [overview, setOverview] = useState(null);

	const dispatch = useDispatch();
	const usersFromStore = useSelector(selectUsersData);
	const usersStatus = useSelector(selectUsersStatus);

	// UserSelectorDialog needs the full user list to work at all, so kick off
	// the same fetch here too -- this also lets us pre-select whoever checked
	// in last, same as the old plain <select> used to.
	useEffect(() => {
		if (usersStatus === "idle") {
			dispatch(fetchUsers());
		}
	}, [usersStatus, dispatch]);

	useEffect(() => {
		async function preloadLastUser() {
			if (selectedUser) return; // don't clobber a user already chosen this session
			if (!usersFromStore || usersFromStore.length === 0) return;

			const lastOperator = await GetLastUser();
			if (!lastOperator) return;

			const match = usersFromStore
				.filter((u) => (u.userGroup || "").toLowerCase().includes("shipping"))
				.find((u) => u.operator === lastOperator);

			if (match) {
				setSelectedUser(match);
				setCurrentUser(match.operator);
			}
		}

		preloadLastUser();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [usersFromStore]);

	useEffect(() => {
		async function loadReceiptInformation() {
			var newInfo = defaultValues;
			newInfo.dateReceied = getFormattedDateTime();
			try {
				let response = await GetOrCreateReceiptInformation(props.scanData);
				if (!response || response.userId === null) {
					NotificationManager.info("New scan", "Sample Receipt");
					newInfo.recordId = response.recordId;
					newInfo.identifier = response.identifier;
					newInfo.isKitPaid = response.isKitPaid;
					newInfo.willRushPay = response.willRushPay;
					newInfo.orderPayType = response.orderPayType;
					newInfo.checkInSheetCount = response.checkInSheetCount;
					newInfo.totalSampleCount = response.totalSampleCount;
					newInfo.remainingSampleCount = response.remainingSampleCount;
					newInfo.allSamplesSubmitted = response.allSamplesSubmitted;
					newInfo.unsubmittedSamples = response.unsubmittedSamples;
				} else {
					NotificationManager.info(
						"An existing scan was found",
						"Sample Receipt"
					);
					response.dateReceied = getFormattedDateTime(response.dateReceied);
					newInfo = response;
				}
			} catch (err) {
				NotificationManager.error(err.toString());
			}
			setInfo(newInfo);
			setOverview({ ...newInfo });
			// Default to "submit everything still outstanding" -- most check-ins
			// cover the whole remaining batch in one go.
			setSelectedSampleIds((newInfo.unsubmittedSamples || []).map((s) => s.sampleRecordId));
			// Anything already submitted means the user might have come here to fix a
			// previous sheet rather than add another, so ask before assuming.
			setMode((newInfo.submittedSheets || []).length > 0 ? "choose" : "new");
		}

		loadReceiptInformation();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleUserSelect = (user) => {
		if (user) {
			setSelectedUser(user);
			setCurrentUser(user.operator);
			SetLastUser(user.operator);
			setUserRequiredError(false);
		}
	};

	const handleStartNew = () => {
		setMode("new");
	};

	const handleEditSheet = async (sheet) => {
		SetLoading(true);
		try {
			const sheetInfo = await GetCheckInSheet(props.scanData, sheet.recordId);
			sheetInfo.dateReceied = getFormattedDateTime(sheetInfo.dateReceied);
			setInfo(sheetInfo);
			setEditingSheet({ recordId: sheet.recordId, sheetNumber: sheetInfo.sheetNumber });
			// A submitted sheet's samples are fixed, so there's no selection to carry.
			setSelectedSampleIds([]);

			// Default to whoever originally signed the sheet rather than making the
			// user re-pick just to save a correction.
			const originalOperator = (sheetInfo.userId || "").trim();
			if (originalOperator) {
				const match = (usersFromStore || []).find(
					(u) => (u.operator || "").trim() === originalOperator
				);
				// Fall back to a display-only stand-in so the User field always shows the
				// operator we'd actually save, even if they're no longer in the shipping
				// list. The user can still click through to change it.
				setSelectedUser(match || { operator: originalOperator, aliasName: "" });
				setCurrentUser(originalOperator);
				setUserRequiredError(false);
			}

			setMode("edit");
		} catch (err) {
			NotificationManager.error("Error loading the check-in sheet. " + err, "Error");
		}
		SetLoading(false);
	};

	const onSubmit = async (data) => {
		if (!currentUser) {
			setUserRequiredError(true);
			NotificationManager.error("Please select a user before submitting.", "User required");
			return;
		}

		SetLoading(true);
		if (data.UserId == null) {
			data.userId = currentUser;
		}

		try {
			if (mode === "edit" && editingSheet) {
				// Sample assignments are fixed server-side for an existing sheet.
				delete data.selectedSampleRecordIds;
				let response = await UpdateExistingCheckInSheet(
					props.scanData,
					editingSheet.recordId,
					data
				);
				if (response === 200) {
					NotificationManager.success(
						`Check-In Sheet #${editingSheet.sheetNumber} updated.`,
						"Sample Receipt"
					);
					props.clearScan();
				}
			} else {
				data.selectedSampleRecordIds = selectedSampleIds;
				let response = await UpdateReceiptInformation(data);
				if (response === 200) props.clearScan();
			}
		} catch (err) {
			NotificationManager.error("Error submitting. " + err, "Error");
		}
		SetLoading(false);
	};

	const onResetHeadSpace = () => {		
		let infoValue = info;
		infoValue.headSpacePresent = null
		setInfo(infoValue)
	};

	const handleBackClick = () => {
		// When the order has submitted sheets, step back to the picker instead of
		// closing outright -- the user may have opened the wrong sheet.
		if (mode !== "choose" && (overview?.submittedSheets || []).length > 0) {
			setInfo(overview);
			setEditingSheet(null);
			setSelectedSampleIds(
				(overview.unsubmittedSamples || []).map((s) => s.sampleRecordId)
			);
			setMode("choose");
			return;
		}

		props.clearScan();
	};

	return (
		<>
			<div className="container">
				<div className="section">
					<div className="columns">
						<div className="column">
							<button
								className="button is-warning mr-5"
								onClick={handleBackClick}
							>
								Back
							</button>
							<span style={{ fontSize: "18pt", fontWeight: "bold" }}>
								{props.scanData}
							</span>
						</div>
						<div className="column is-one-quarter">
							<TextField
								label="User"
								required
								error={userRequiredError}
								helperText={userRequiredError ? "Please select a user" : ""}
								value={
									selectedUser
										? `${selectedUser.operator.trim()} - ${selectedUser.aliasName?.trim()}`
										: "Click to select user"
								}
								size="small"
								fullWidth
								onClick={() => setUserSelectorOpen(true)}
								InputProps={{
									readOnly: true,
									style: { cursor: "pointer" },
									startAdornment: !selectedUser && (
										<InputAdornment position="start">
											<PersonIcon color={userRequiredError ? "error" : "primary"} />
										</InputAdornment>
									),
									endAdornment: selectedUser && (
										<InputAdornment position="end">
											<EditIcon color="primary" sx={{ fontSize: 20 }} />
										</InputAdornment>
									),
								}}
							/>
						</div>
					</div>
				</div>
				<UserSelectorDialog
					open={userSelectorOpen}
					onClose={() => setUserSelectorOpen(false)}
					onSelect={handleUserSelect}
					multiSelect={false}
					filter="SHIPPING"
				/>
				{overview && mode === "choose" ? (
					<CheckInSheetSelector
						orderId={props.scanData}
						submittedSheets={overview.submittedSheets}
						totalSampleCount={overview.totalSampleCount}
						remainingSampleCount={overview.remainingSampleCount}
						onStartNew={handleStartNew}
						onEditSheet={handleEditSheet}
						onBack={handleBackClick}
					/>
				) : info && mode !== "edit" && info.allSamplesSubmitted ? (
					<AllSamplesSubmitted
						orderId={props.scanData}
						checkInSheetCount={info.checkInSheetCount}
						totalSampleCount={info.totalSampleCount}
						onBack={handleBackClick}
					/>
				) : info ? (
					<SampleScannedForm
						model={info}
						isLoading={loading}
						onSubmit={onSubmit}
						onResetHeadSpace={onResetHeadSpace}
						webUrl={props.webUrl}
						unsubmittedSamples={info.unsubmittedSamples}
						selectedSampleIds={selectedSampleIds}
						onSelectedSampleIdsChange={setSelectedSampleIds}
						checkInSheetCount={info.checkInSheetCount}
						totalSampleCount={info.totalSampleCount}
						remainingSampleCount={info.remainingSampleCount}
						isEditing={mode === "edit"}
						sheetSamples={info.samples}
						sheetNumber={editingSheet?.sheetNumber}
					/>
				) : (
					<progress className="progress is-large is-info" max="100">60%</progress>
				)}
			</div>
		</>
	);
}
